/**
 * Permission Hook
 * 
 * ใช้สำหรับตรวจสอบสิทธิ์การเข้าถึงเมนู, หน้า, และ action
 * Phase 1: Menu-level permissions
 */

import { useMemo, useState, useEffect } from 'react'
import { useAuth } from '@/components/auth'
import { AdminGroup } from '@/domain/admin/admin.types'
import { RoleService, MenuPermissionConfig } from '@/domain/role/role.service'

// ============================================
// Menu Permissions Config
// ============================================
// กำหนดว่าแต่ละ role เห็นเมนูอะไร
// Super Admin = 0, Admin = 1, Partner = 3

const MENU_PERMISSIONS: Record<string, AdminGroup[]> = {
  // 📊 หน้าหลัก
  '/dashboard': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],

  // 💰 จัดการสมาชิกและการเงิน
  '/members': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/transactions': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/transactions/smart-cashier': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/transactions/slips': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/banks': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/promotions': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  
  // คืนยอดเสีย
  '/lossback': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/lossback/manage': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/lossback/records': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  
  '/member-groups': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],

  // 🎮 เกมและการตลาด
  '/games': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/games/list': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/games/providers': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/games/categories': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/games/settings': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/games/report': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  
  '/marketing': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/banners': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/campaigns': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/popups': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/sms': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/line': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/email': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/push': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/marketing/landing': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],

  // 📊 รายงาน
  '/reports': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/reports/financial': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/reports/monthly': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/reports/members': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/reports/transactions': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/reports/banks': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],

  // 🤝 พันธมิตร (Partner เน้นที่ส่วนนี้)
  '/affiliates': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/affiliates/list': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/affiliates/commissions': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/affiliates/links': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/affiliates/payouts': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
  '/affiliates/report': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],

  // ⚙️ ตั้งค่าและระบบ
  '/cron-jobs': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/settings': [AdminGroup.SUPER_ADMIN], // เฉพาะ Super Admin
  '/admins': [AdminGroup.SUPER_ADMIN], // เฉพาะ Super Admin
  '/acl': [AdminGroup.SUPER_ADMIN], // เฉพาะ Super Admin
  '/audit-log': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/devices': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN],
  '/changelog': [AdminGroup.SUPER_ADMIN, AdminGroup.ADMIN, AdminGroup.PARTNER],
}

// ============================================
// Permission Hook
// ============================================

export function usePermission() {
  const { user, sessionToken } = useAuth()
  const [rolePermissionsCache, setRolePermissionsCache] = useState<Record<number, MenuPermissionConfig[]>>({})
  const [refreshKey, setRefreshKey] = useState(0) // ✅ ใช้สำหรับ trigger refresh
  
  // ดึง adminGroup จาก user
  // ถ้าไม่มี user หรือไม่มี adminGroup ให้ default เป็น PARTNER (สิทธิ์น้อยที่สุด)
  const adminGroup = useMemo(() => {
    if (!user) return AdminGroup.PARTNER
    
    // ตรวจสอบจาก adminGroup field (camelCase)
    if (user.adminGroup !== undefined) {
      return user.adminGroup as AdminGroup
    }
    
    // ตรวจสอบจาก admin_group field (snake_case) - สำหรับ backward compatible
    if ((user as any).admin_group !== undefined) {
      return (user as any).admin_group as AdminGroup
    }
    
    // Fallback: ตรวจสอบจาก type
    if (user.type === 'super_admin') {
      return AdminGroup.SUPER_ADMIN
    }
    if (user.type === 'admin') {
      return AdminGroup.ADMIN
    }
    
    // Default: Partner
    return AdminGroup.PARTNER
  }, [user])

  // ✅ Load role permissions from database when adminGroup changes or refreshKey changes
  useEffect(() => {
    if (!sessionToken || adminGroup === undefined || adminGroup === null) return
    
    // ✅ Load role permissions from database (always reload to get latest)
    RoleService.getRolePermissionsWithActions(sessionToken, adminGroup)
      .then(permissions => {
        setRolePermissionsCache(prev => ({
          ...prev,
          [adminGroup]: permissions,
        }))
      })
      .catch(error => {
        console.error('[usePermission] Error loading role permissions:', error)
        // Fallback to default permissions
        const defaultPermissions = RoleService.getDefaultPermissionsWithActions(adminGroup)
        setRolePermissionsCache(prev => ({
          ...prev,
          [adminGroup]: defaultPermissions,
        }))
      })
  }, [sessionToken, adminGroup, refreshKey]) // ✅ เพิ่ม refreshKey เพื่อ trigger reload

  // ✅ Listen for permission updates (triggered by ACL page)
  useEffect(() => {
    // ✅ Handle localStorage event (cross-tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'role_permissions_updated' && e.newValue) {
        const updatedAdminGroup = parseInt(e.newValue, 10)
        if (updatedAdminGroup === adminGroup) {
          // ✅ Clear cache และ reload
          setRolePermissionsCache(prev => {
            const newCache = { ...prev }
            delete newCache[updatedAdminGroup]
            return newCache
          })
          setRefreshKey(prev => prev + 1) // ✅ Trigger reload
        }
      }
    }

    // ✅ Handle custom event (same-tab)
    const handleCustomEvent = (e: CustomEvent) => {
      const updatedAdminGroup = e.detail?.adminGroup
      if (updatedAdminGroup === adminGroup) {
        // ✅ Clear cache และ reload
        setRolePermissionsCache(prev => {
          const newCache = { ...prev }
          delete newCache[updatedAdminGroup]
          return newCache
        })
        setRefreshKey(prev => prev + 1) // ✅ Trigger reload
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('rolePermissionsUpdated', handleCustomEvent as EventListener)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('rolePermissionsUpdated', handleCustomEvent as EventListener)
    }
  }, [adminGroup])

  /**
   * ตรวจสอบว่าสามารถเข้าถึงเมนู/หน้าได้หรือไม่
   */
  const canAccessMenu = useMemo(() => {
    return (path: string): boolean => {
      // ถ้าไม่มี user = ไม่มีสิทธิ์
      if (!user) return false
      
      // ✅ ตรวจสอบจาก user.permissions ก่อน (ถ้ามีและไม่ใช่ empty array)
      // ถ้า admin มี permissions ตั้งค่าเอง → ใช้ permissions ของ admin (override role)
      if (user.permissions) {
        // ถ้าเป็น array (string[])
        if (Array.isArray(user.permissions)) {
          // ถ้าเป็น empty array = ยังไม่ได้ตั้งค่า → fallback ไปใช้ role permissions
          if (user.permissions.length === 0) {
            // Fallback to role permissions (continue below)
          } else {
            // มี permissions → ตรวจสอบ
            return user.permissions.includes('all') || user.permissions.includes(path)
          }
        }
        
        // ถ้าเป็น object (Record<string, boolean>)
        if (typeof user.permissions === 'object' && !Array.isArray(user.permissions)) {
          return user.permissions[path] === true || user.permissions['all'] === true
        }
      }
      
      // ✅ Fallback: ตรวจสอบจาก Role Permissions (AdminGroup)
      // (ใช้เมื่อ permissions เป็น undefined, null, หรือ empty array)
      // Super Admin เห็นทุกอย่าง
      if (adminGroup === AdminGroup.SUPER_ADMIN) {
        return true
      }
      
      // ✅ ดึง permissions จาก cache (โหลดจาก database แล้ว)
      const cachedPermissions = rolePermissionsCache[adminGroup]
      
      if (cachedPermissions && cachedPermissions.length > 0) {
        // ✅ มี permissions จาก database → ใช้ permissions จาก database เท่านั้น
        const permConfig = cachedPermissions.find(p => p.path === path)
        if (permConfig) {
          return permConfig.enabled // ✅ ใช้ enabled จาก database
        }
        // ถ้าไม่มี path นี้ใน permissions → ไม่มีสิทธิ์ (return false)
        return false
      }
      
      // ✅ ถ้ายังไม่มี cache (กำลังโหลด) → ใช้ default permissions ชั่วคราว
      // แต่จะไม่ fallback ไปใช้ MENU_PERMISSIONS config เพื่อป้องกันการแสดงเมนูที่ปิด
      const rolePermissions = RoleService.getRolePermissions(adminGroup)
      
      // ตรวจสอบจาก role permissions (array of paths)
      if (rolePermissions.includes('all')) {
        return true
      }
      
      if (rolePermissions.includes(path)) {
        return true
      }
      
      // ✅ ไม่ fallback ไปใช้ MENU_PERMISSIONS config ถ้ามี permissions ใน database
      // เพื่อป้องกันการแสดงเมนูที่ปิด
      return false
    }
  }, [user, adminGroup, rolePermissionsCache])

  /**
   * ตรวจสอบว่าสามารถเข้าถึง submenu ได้หรือไม่
   */
  const canAccessSubmenu = useMemo(() => {
    return (parentPath: string, childPath: string): boolean => {
      // ตรวจสอบทั้ง parent และ child
      return canAccessMenu(parentPath) && canAccessMenu(childPath)
    }
  }, [canAccessMenu])

  /**
   * ตรวจสอบว่าเป็น Super Admin หรือไม่
   */
  const isSuperAdmin = useMemo(() => {
    return adminGroup === AdminGroup.SUPER_ADMIN
  }, [adminGroup])

  /**
   * ตรวจสอบว่าเป็น Admin หรือไม่ (รวม Super Admin)
   */
  const isAdmin = useMemo(() => {
    return adminGroup === AdminGroup.SUPER_ADMIN || adminGroup === AdminGroup.ADMIN
  }, [adminGroup])

  /**
   * ตรวจสอบว่าเป็น Partner หรือไม่
   */
  const isPartner = useMemo(() => {
    return adminGroup === AdminGroup.PARTNER
  }, [adminGroup])

  return {
    adminGroup,
    canAccessMenu,
    canAccessSubmenu,
    isSuperAdmin,
    isAdmin,
    isPartner,
  }
}

// ============================================
// Export Config (สำหรับใช้ที่อื่น)
// ============================================

export { MENU_PERMISSIONS }
